import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GripVertical, MapPin, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText, media } from "@/lib/api";
import { SCHEMAS } from "@/admin/schemas";
import { confirmPublic, FileField, MultiMember, PhotoField, PhotosField, PrivacyBadge, PrivacySelect } from "@/admin/fields";

const FP = ["name", "photo", "dates", "bio", "businesses", "roles", "contact"];

const Rows = ({ value = [], onChange, cols, members }) => (
  <div className="space-y-2">
    {value.map((r, i) => (
      <div key={i} className="flex gap-2">
        {cols.map(([k, t]) => t === "member"
          ? <select key={k} className="field" value={r[k] || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))}><option value="">Choose member</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
          : <input key={k} className="field" placeholder={k} value={r[k] || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />)}
        <button type="button" className="lb-btn !w-10 shrink-0" onClick={() => onChange(value.filter((_, j) => j !== i))}><X size={14} /></button>
      </div>
    ))}
    <button type="button" className="btn-ghost btn-sm" onClick={() => onChange([...value, {}])}><Plus size={14} /> Add row</button>
  </div>
);

const Field = ({ f, item, set, members }) => {
  const [key, label, type = "text", opt] = f;
  const v = item[key];
  const tid = `field-${key}`;
  let input;
  if (type === "textarea") input = <textarea className="field min-h-[110px]" value={v || ""} onChange={(e) => set(key, e.target.value)} data-testid={tid} />;
  else if (type === "number") input = <input type="number" step="any" className="field" value={v ?? ""} onChange={(e) => set(key, e.target.value === "" ? null : Number(e.target.value))} data-testid={tid} />;
  else if (type === "bool") input = <input type="checkbox" className="w-5 h-5 accent-[#C9A24B]" checked={!!v} onChange={(e) => set(key, e.target.checked)} data-testid={tid} />;
  else if (type === "date") input = <input type="date" className="field" value={v || ""} onChange={(e) => set(key, e.target.value)} data-testid={tid} />;
  else if (type === "select") input = <select className="field" value={v || ""} onChange={(e) => set(key, e.target.value)} data-testid={tid}>{opt.map((o) => <option key={o}>{o}</option>)}</select>;
  else if (type === "privacy") input = <PrivacySelect value={v} onChange={(x) => set(key, x)} testid={tid} allowPublic={!opt?.noPublic && !item.is_child} />;
  else if (type === "member") input = <select className="field" value={v || ""} onChange={(e) => set(key, e.target.value || null)} data-testid={tid}><option value="">None</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>;
  else if (type === "members") input = <MultiMember members={members} value={v || []} onChange={(x) => set(key, x)} testid={tid} />;
  else if (type === "photo") input = <PhotoField value={v} url={item[`${key}_url`]} onChange={(x) => set(key, x)} testid={tid} />;
  else if (type === "photos") input = <PhotosField value={v || []} onChange={(x) => set(key, x)} testid={tid} />;
  else if (type === "file") input = <FileField value={v} onChange={(x) => set(key, x)} testid={tid} />;
  else if (type === "roles") input = <Rows value={v} onChange={(x) => set(key, x)} cols={[["member", "member"], ["role", "text"]]} members={members} />;
  else if (type === "history") input = <Rows value={v} onChange={(x) => set(key, x)} cols={[["year", "text"], ["text", "text"]]} members={members} />;
  else if (type === "fieldprivacy") input = (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{FP.map((k) => (
      <div key={k}><p className="text-[11px] text-[#F6EFE0]/75 mb-1 capitalize">{k}</p>
        <PrivacySelect value={(v || {})[k]} allowPublic={!item.is_child} onChange={(x) => set(key, { ...(v || {}), [k]: x })} testid={`fp-${k}`} /></div>))}</div>
  );
  else input = <input className="field" value={v || ""} onChange={(e) => set(key, e.target.value)} data-testid={tid} />;
  return <div className={type === "bool" ? "flex items-center gap-3" : ""}><label className="label">{label}</label>{input}</div>;
};

export const CollectionEditor = ({ coll }) => {
  const S = SCHEMAS[coll];
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [dragI, setDragI] = useState(null);
  const load = async () => {
    const [a, m] = await Promise.all([api.get(`/admin/c/${coll}`), api.get("/admin/c/members")]);
    setItems(a.data || []); setMembers(m.data || []);
  };
  useEffect(() => { load().catch(() => {}); }, [coll]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/c/${coll}/${edit.id}`, edit); else await api.post(`/admin/c/${coll}`, edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const del = async (x) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/c/${coll}/${x.id}`); load(); };
  const drop = async (to) => {
    if (dragI === null || dragI === to) return;
    const next = [...items]; const [it] = next.splice(dragI, 1); next.splice(to, 0, it);
    setItems(next); setDragI(null);
    await api.post(`/admin/c/${coll}/reorder`, { ids: next.map((x) => x.id) });
  };
  const quickPrivacy = async (x, v) => { if (!confirmPublic(v)) return; await api.put(`/admin/c/${coll}/${x.id}`, { privacy: v }); load(); };
  const geocode = async () => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(`${edit.name || ""} ${edit.region || ""}`)}`);
      const j = await r.json();
      if (j[0]) setEdit({ ...edit, lat: Number(Number(j[0].lat).toFixed(2)), lng: Number(Number(j[0].lon).toFixed(2)) }); else toast.error("Place not found");
    } catch (e) { toast.error("Could not reach the map service"); }
  };
  return (
    <div data-testid={`admin-coll-${coll}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">{S.title}</h2>
        <button className="btn-gold btn-sm" onClick={() => setEdit({ privacy: coll === "members" ? "hidden" : "hidden" })} data-testid={`admin-add-${coll}`}><Plus size={14} /> Add</button>
      </div>
      <ul className="space-y-2">
        {items.map((x, i) => (
          <li key={x.id} draggable onDragStart={() => setDragI(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
            className={`flex items-center gap-3 p-3 rounded-xl border border-[#C9A24B]/20 bg-[#0F2A1D]/60 ${dragI === i ? "opacity-50" : ""}`} data-testid={`admin-row-${coll}-${i}`}>
            <GripVertical size={16} className="text-[#C9A24B] cursor-grab shrink-0" />
            {(x.photo_url || x.thumb_url) && <img src={media(x.photo_url || x.thumb_url)} alt="" className="w-14 h-10 object-cover rounded shrink-0" />}
            <div className="flex-1 min-w-0"><p className="truncate">{S.label(x)}</p><div className="flex gap-2 mt-1 flex-wrap"><PrivacyBadge v={x.privacy} />{x.sample && <span className="badge-sample">Sample content</span>}{x.is_child && <span className="text-[10px] text-[#F2B8A0]">CHILD · NEVER PUBLIC</span>}</div></div>
            <select className="field !w-36 !py-1.5 text-sm hidden sm:block" value={x.privacy || "hidden"} onChange={(e) => quickPrivacy(x, e.target.value)} data-testid={`admin-quick-privacy-${coll}-${i}`}>
              <option value="hidden">Hidden</option><option value="family">Family only</option>{!x.is_child && !["businesses", "investments", "governance"].includes(coll) && <option value="public">Public</option>}
            </select>
            <button className="lb-btn !w-9 !h-9" onClick={() => setEdit({ ...x })} data-testid={`admin-edit-${coll}-${i}`}><Pencil size={14} /></button>
            <button className="lb-btn !w-9 !h-9" onClick={() => del(x)} data-testid={`admin-delete-${coll}-${i}`}><Trash2 size={14} /></button>
          </li>
        ))}
      </ul>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl bg-[#0B1D14] border-[#C9A24B]/40 text-[#F6EFE0] max-h-[88vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{edit?.id ? "Edit" : "Add"} · {S.title}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-5">
              {S.fields.map((f) => <Field key={f[0]} f={f} item={edit} members={members.filter((m) => m.id !== edit.id)} set={(k, v) => setEdit((e) => ({ ...e, [k]: v }))} />)}
              {coll === "places" && <button type="button" className="btn-ghost btn-sm w-fit" onClick={geocode} data-testid="admin-geocode-btn"><MapPin size={14} /> Find position on the map</button>}
              <button className="btn-gold w-fit" onClick={save} data-testid="admin-save-btn">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
